import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { EntitlementsProvider } from "@/contexts/EntitlementsContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import { BottomTabBar } from "@/components/BottomTabBar";
import { Layout } from "@/components/Layout";
import { Landing } from "@/pages/Landing";
import { Login } from "@/pages/Login";
import { Feed } from "@/pages/Feed";
import { Groups } from "@/pages/Groups";
import { GroupPage } from "@/pages/GroupPage";
import { GroupCreate } from "@/pages/GroupCreate";
import { Treatment } from "@/pages/Treatment";
import { Tratamentos } from "@/pages/Tratamentos";
import { Profile } from "@/pages/Profile";
import { ProfileEdit } from "@/pages/ProfileEdit";
import { Meetings } from "@/pages/Meetings";
import { MeetingDetail } from "@/pages/MeetingDetail";
import { MeetingCreate } from "@/pages/MeetingCreate";
import { InviteAccept } from "@/pages/InviteAccept";
import { MeetingRoom } from "@/pages/MeetingRoom";
import { ProfessionalProfile } from "@/pages/ProfessionalProfile";
import CoursePAREIAD from "@/pages/CoursePAREIAD";
import { Checkout } from "@/pages/Checkout";
import { Shop } from "@/pages/Shop";
import { Ranking } from "@/pages/Ranking";
import { WalletPage } from "@/pages/WalletPage";
import { GardenPage } from "@/pages/GardenPage";
import { GardenCollectiblesPage } from "@/pages/GardenCollectiblesPage";
import { NFTTreePage } from "@/pages/NFTTreePage";
import { OpportunitiesPage } from "@/pages/OpportunitiesPage";
import { PrivacyPolicyPage } from "@/pages/PrivacyPolicyPage";
import { TermsOfUsePage } from "@/pages/TermsOfUsePage";
import { ImportantNoticePage } from "@/pages/ImportantNoticePage";
import { InstallGuidePage } from "@/pages/InstallGuidePage";
import { AdminLoginPage } from "@/pages/admin/AdminLoginPage";
import { AdminLayout, AdminProtectedRoute } from "@/pages/admin/AdminDashboard";
import { PWAInstallBanner } from "@/components/PWAInstallBanner";
import AgendaPage from "@/pages/AgendaPage";
import BookingsPage from "@/pages/BookingsPage";
import ConsultationRoom from "@/pages/ConsultationRoom";
import ProfessionalPublicProfile from "@/pages/ProfessionalPublicProfile";
import ProfessionalCardPage from "@/pages/ProfessionalCardPage";
import PareiAdGatePage from "@/pages/PareiAdGatePage";
import VerifiedProfessionalsPage from "@/pages/VerifiedProfessionalsPage";
import QuizRecuperacao from "@/pages/QuizRecuperacao";
import QuizFamiliares from "@/pages/QuizFamiliares";
import QuizProfissionais from "@/pages/QuizProfissionais";
import DailyActivities from "@/pages/DailyActivities";
import CourseFamilyPage from "@/pages/CourseFamilyPage";
import PremiumPage from "@/pages/PremiumPage";
import CourseStandbyPage from "@/pages/CourseStandbyPage";
import AriaPage from "@/pages/AriaPage";
import PlanosPage from "@/pages/PlanosPage";
import ProgramasPage from "@/pages/ProgramasPage";
import Programa90Page from "@/pages/Programa90Page";
import Programa180Page from "@/pages/Programa180Page";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import GoogleAuthCallback from "@/pages/GoogleAuthCallback";
import AdminForgotPasswordPage from "@/pages/admin/AdminForgotPasswordPage";
import AdminResetPasswordPage from "@/pages/admin/AdminResetPasswordPage";
import AwareLandingPage from "@/pages/AwareLandingPage";
import DashboardPage from "@/pages/DashboardPage";

// Protected Route Component
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Conditional Course Route: admin/premium sees real course, others see standby
function ConditionalCourseRoute({ children }) {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role === 'admin' || user?.is_founder || user?.is_premium) {
    return children;
  }

  return <CourseStandbyPage />;
}

// Referral redirect component
function ReferralRedirect() {
  const { code } = useParams();
  return <Navigate to={`/?ref=${code}`} replace />;
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <EntitlementsProvider>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Landing />} />
              <Route path="/aware" element={<AwareLandingPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/esqueci-senha" element={<ForgotPasswordPage />} />
              <Route path="/redefinir-senha" element={<ResetPasswordPage />} />
              <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />
              <Route path="/quiz/recuperacao" element={<QuizRecuperacao />} />
              <Route path="/quiz/familiares" element={<QuizFamiliares />} />
              <Route path="/quiz/profissionais" element={<QuizProfissionais />} />

              {/* Protected routes */}
              <Route path="/app" element={<ProtectedRoute><Layout><Navigate to="/feed" replace /></Layout></ProtectedRoute>} />
              <Route path="/feed" element={<ProtectedRoute><Layout><Feed /></Layout></ProtectedRoute>} />
              <Route path="/dashboard" element={<ProtectedRoute><Layout><DashboardPage /></Layout></ProtectedRoute>} />
              <Route path="/grupos" element={<ProtectedRoute><Layout><Groups /></Layout></ProtectedRoute>} />
              <Route path="/grupos/criar" element={<ProtectedRoute><Layout><GroupCreate /></Layout></ProtectedRoute>} />
              <Route path="/grupos/:slug" element={<ProtectedRoute><Layout><GroupPage /></Layout></ProtectedRoute>} />
              <Route path="/tratamento" element={<ProtectedRoute><Layout><Treatment /></Layout></ProtectedRoute>} />
              
              {/* /academia is the new route, /tratamentos redirects to it */}
              <Route path="/academia" element={<ProtectedRoute><Layout><Tratamentos /></Layout></ProtectedRoute>} />
              <Route path="/tratamentos" element={<Navigate to="/academia" replace />} />

              <Route path="/atividades" element={<ProtectedRoute><DailyActivities /></ProtectedRoute>} />

              {/* ARIA Chat - dedicated page */}
              <Route path="/aria" element={<ProtectedRoute><Layout><AriaPage /></Layout></ProtectedRoute>} />

              {/* Premium Plans */}
              <Route path="/planos" element={<ProtectedRoute><Layout><PlanosPage /></Layout></ProtectedRoute>} />

              {/* Programas de Recuperação */}
              <Route path="/programas" element={<ProtectedRoute><Layout><ProgramasPage /></Layout></ProtectedRoute>} />
              <Route path="/programa/90-dias" element={<ProtectedRoute><Layout><Programa90Page /></Layout></ProtectedRoute>} />
              <Route path="/programa/180-dias" element={<ProtectedRoute><Layout><Programa180Page /></Layout></ProtectedRoute>} />

              {/* Courses */}
              <Route path="/formacao/parei-ad" element={<ProtectedRoute><PareiAdGatePage /></ProtectedRoute>} />
              <Route path="/curso/profissional" element={<Navigate to="/formacao/parei-ad" replace />} />
              <Route path="/curso/familiar" element={<ConditionalCourseRoute><CourseFamilyPage /></ConditionalCourseRoute>} />

              <Route path="/carteira" element={<ProtectedRoute><Layout><WalletPage /></Layout></ProtectedRoute>} />
              <Route path="/vsawallet" element={<ProtectedRoute><Layout><WalletPage /></Layout></ProtectedRoute>} />
              <Route path="/jardim" element={<ProtectedRoute><Layout><GardenPage /></Layout></ProtectedRoute>} />
              <Route path="/colecao" element={<ProtectedRoute><Layout><GardenCollectiblesPage /></Layout></ProtectedRoute>} />
              <Route path="/minha-arvore" element={<ProtectedRoute><Layout><NFTTreePage /></Layout></ProtectedRoute>} />
              <Route path="/oportunidades" element={<ProtectedRoute><Layout><OpportunitiesPage /></Layout></ProtectedRoute>} />
              <Route path="/me" element={<ProtectedRoute><Layout><Profile /></Layout></ProtectedRoute>} />
              <Route path="/comunidade/profissionais" element={<ProtectedRoute><Layout><VerifiedProfessionalsPage /></Layout></ProtectedRoute>} />
              <Route path="/profile/professional" element={<ProtectedRoute><Layout><ProfessionalCardPage /></Layout></ProtectedRoute>} />
              <Route path="/me/edit" element={<ProtectedRoute><Layout><ProfileEdit /></Layout></ProtectedRoute>} />
              <Route path="/reunioes" element={<ProtectedRoute><Layout><Meetings /></Layout></ProtectedRoute>} />
              <Route path="/reunioes/criar" element={<ProtectedRoute><Layout><MeetingCreate /></Layout></ProtectedRoute>} />
              <Route path="/reunioes/:slug" element={<ProtectedRoute><MeetingDetail /></ProtectedRoute>} />
              <Route path="/reunioes/:meetingId/room" element={<ProtectedRoute><MeetingRoom /></ProtectedRoute>} />
              <Route path="/reunioes/invite/:token" element={<ProtectedRoute><InviteAccept /></ProtectedRoute>} />
              <Route path="/profissionais/perfil" element={<ProtectedRoute><Layout><ProfessionalProfile /></Layout></ProtectedRoute>} />
              <Route path="/profissionais/:slug" element={<ProtectedRoute><Layout><ProfessionalPublicProfile /></Layout></ProtectedRoute>} />
              <Route path="/profissionais/curso-condensado" element={<Navigate to="/curso/profissional" replace />} />
              <Route path="/profissionais/agenda" element={<ProtectedRoute><Layout><AgendaPage /></Layout></ProtectedRoute>} />
              <Route path="/me/agendamentos" element={<ProtectedRoute><Layout><BookingsPage /></Layout></ProtectedRoute>} />
              <Route path="/sala-atendimento" element={<ProtectedRoute><Layout><ConsultationRoom /></Layout></ProtectedRoute>} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/premium" element={<PremiumPage />} />
              <Route path="/loja" element={<Layout><Shop /></Layout>} />
              <Route path="/ranking" element={<ProtectedRoute><Layout><Ranking /></Layout></ProtectedRoute>} />

              {/* Public - Legal */}
              <Route path="/privacidade" element={<PrivacyPolicyPage />} />
              <Route path="/termos" element={<TermsOfUsePage />} />
              <Route path="/aviso-importante" element={<ImportantNoticePage />} />
              <Route path="/instalar" element={<InstallGuidePage />} />

              {/* Admin Portal */}
              <Route path="/admin-login" element={<AdminLoginPage />} />
              <Route path="/admin-esqueci-senha" element={<AdminForgotPasswordPage />} />
              <Route path="/admin-redefinir-senha" element={<AdminResetPasswordPage />} />
              <Route path="/admin/*" element={<AdminProtectedRoute><AdminLayout /></AdminProtectedRoute>} />

              {/* Referral Route */}
              <Route path="/convite/:code" element={<ReferralRedirect />} />

              {/* Catch all */}
              <Route path="*" element={<Navigate to="/feed" replace />} />
            </Routes>
            <Toaster position="top-right" />
            <BottomTabBar />
            <PWAInstallBanner />
          </EntitlementsProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
